/**
 * Hero screen view-model (pure, DOM-free, unit-tested in heroModel.test.ts): the pentagon radar,
 * the bag's gear list (best first), one-tap Auto-equip / Salvage, the gem stash as slots and the
 * Materials tab (every wallet resource lives there now; the top bar shows only 3 currencies).
 */
import type { ClassId } from '@keystone/sim';
import {
  type CompiledCharacter,
  type Gem,
  type GemColor,
  type Item,
  type Profile,
  type ResourceBundle,
  GEM_COLORS,
  ITEM_SLOTS,
  MAX_GEM_RANK,
  equippedGear,
  fuseGems,
  gemCount,
  itemScore,
  legendary,
  powerMult,
  salvage,
  unequippedItems,
  wearIfBetter,
} from '@keystone/progression';
import { type ResourceKey, RESOURCE_META } from '../forge/resourceText';

/** Enhancement level shown as "+N" on a slot: temper steps plus masterwork steps. */
export function enhanceLevel(item: Pick<Item, 'forge' | 'masterwork'>): number {
  return (item.forge?.steps ?? 0) + (item.masterwork ?? 0);
}

// ---------------------------------------------------------------------------
// Radar
// ---------------------------------------------------------------------------

export const RADAR_LABELS = ['Damage', 'Survival', 'Utility', 'Speed', 'Burst'] as const;

/** A secondary stat this high (fraction) counts as "full" on the radar. */
const SECONDARY_FULL = 0.3;
const sec = (v: number): number => Math.max(0, Math.min(1, v / SECONDARY_FULL));
const clamp01 = (v: number): number => (Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0);

/**
 * Five 0..1 axes (Damage, Survival, Utility, Speed, Burst) for the pentagon. Item level is
 * normalised to the current tier cap (gear at the cap fills the item-level share), secondaries to
 * 30%. Every axis keeps a small floor so a fresh hero still draws a visible shape.
 */
export function radarAxes(c: Pick<CompiledCharacter, 'sheet' | 'stats'>, tierCap: number): number[] {
  const ilvl = clamp01(powerMult(c.sheet.avgIlvl) / powerMult(Math.max(200, tierCap)));
  const s = c.sheet.secondaries;
  const crit = sec(c.stats.crit ?? s.crit ?? 0);
  const haste = sec(c.stats.haste ?? s.haste ?? 0);
  const exp = sec(s.expertise ?? 0);
  const spirit = sec(s.spirit ?? 0);
  const gems = clamp01(GEM_COLORS.reduce((a, g) => a + (c.sheet.gemPower?.[g] ?? 0), 0) / (480 * 3));
  const sets = clamp01(Math.max(0, ...(c.sheet.sets ?? []).map((x) => x.pieces)) / 4);
  const floor = (v: number): number => 0.12 + 0.88 * clamp01(v);
  return [
    floor(0.6 * ilvl + 0.4 * crit),
    floor(0.7 * ilvl + 0.3 * exp),
    floor(0.3 * ilvl + 0.4 * spirit + 0.3 * gems),
    floor(0.35 * ilvl + 0.65 * haste),
    floor(0.4 * ilvl + 0.3 * crit + 0.3 * sets),
  ];
}

// ---------------------------------------------------------------------------
// Bag
// ---------------------------------------------------------------------------

/** Unequipped gear, best for `classId` first (item score, then newest). */
export function bagGear(p: Profile, classId: ClassId): Item[] {
  return unequippedItems(p).sort((a, b) => itemScore(b, classId) - itemScore(a, classId) || b.id.localeCompare(a.id));
}

/** True when the bag holds an item that beats `classId`'s equipped piece (lobby red dot). */
export function hasBagUpgrade(p: Profile, classId: ClassId): boolean {
  const gear = equippedGear(p, classId);
  return unequippedItems(p).some((i) => {
    const cur = gear[i.slot];
    if (i.legendaryId && legendary(i.legendaryId)?.classId !== classId) return false;
    return !cur || itemScore(i, classId) > itemScore(cur, classId);
  });
}

/** Wear every bag item that beats the current piece. Returns the new profile and how many changed. */
export function autoEquipAll(p0: Profile, classId: ClassId): { profile: Profile; equipped: number } {
  let p = p0;
  for (const item of bagGear(p0, classId)) p = wearIfBetter(p, classId, item.id);
  const before = p0.characters[classId]?.gear ?? {};
  const after = p.characters[classId]?.gear ?? {};
  const equipped = ITEM_SLOTS.filter((s) => before[s] !== after[s]).length;
  return { profile: p, equipped };
}

/**
 * Bag items safe to salvage: not legendary, not a tier piece, and scoring below the equipped
 * piece of that slot for every hero in the party (so no companion would want it either).
 */
export function salvageCandidates(p: Profile): Item[] {
  const heroes = Object.keys(p.characters);
  return unequippedItems(p).filter((item) => {
    if (item.rarity === 'legendary' || item.setId) return false;
    if (p.forge.pendingRefine?.itemId === item.id) return false;
    return heroes.every((c) => {
      const cur = equippedGear(p, c)[item.slot];
      return !!cur && itemScore(item, c) < itemScore(cur, c);
    });
  });
}

/** Salvage every item in `ids`; the yields are summed. Stops quietly on an item that refuses. */
export function salvageAll(p0: Profile, ids: readonly string[]): { profile: Profile; count: number; gained: ResourceBundle } {
  let p = p0;
  let count = 0;
  const gained: ResourceBundle = {};
  for (const id of ids) {
    const r = salvage(p, id);
    if (!r.ok) continue;
    p = r.profile;
    count++;
    addBundle(gained, r.gained ?? {});
  }
  return { profile: p, count, gained };
}

function addBundle(into: ResourceBundle, b: ResourceBundle): void {
  for (const [k, v] of Object.entries(b)) {
    if (k === 'scales' && v && typeof v === 'object') {
      into.scales ??= {};
      for (const [g, n] of Object.entries(v as Record<string, number>)) (into.scales as Record<string, number>)[g] = ((into.scales as Record<string, number>)[g] ?? 0) + n;
    } else if (typeof v === 'number') {
      (into as Record<string, number>)[k] = ((into as Record<string, number>)[k] ?? 0) + v;
    }
  }
}

// ---------------------------------------------------------------------------
// Gems
// ---------------------------------------------------------------------------

export interface GemSlotView {
  gem: Gem;
  count: number;
}

/** Stash gems the player holds, by colour then rank (highest first). */
export function gemSlots(p: Profile): GemSlotView[] {
  const out: GemSlotView[] = [];
  for (const color of GEM_COLORS) {
    for (let rank = MAX_GEM_RANK; rank >= 1; rank--) {
      const count = gemCount(p.gems, { color, rank });
      if (count > 0) out.push({ gem: { color, rank }, count });
    }
  }
  return out;
}

/** Fuse every possible 3→1 (lowest rank first, so fused gems can fuse again). */
export function fuseAll(p0: Profile): { profile: Profile; fused: number } {
  let p = p0;
  let fused = 0;
  for (let rank = 1; rank < MAX_GEM_RANK; rank++) {
    for (const color of GEM_COLORS as readonly GemColor[]) {
      for (let guard = 0; guard < 200; guard++) {
        const r = fuseGems(p, color, rank);
        if (!r.ok) break;
        p = r.profile;
        fused++;
      }
    }
  }
  return { profile: p, fused };
}

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------

export interface MaterialView {
  key: ResourceKey | 'vaultTokens';
  label: string;
  /** Painted icon stem (public/assets/icons). */
  icon: string;
  count: number;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  use: string;
}

const MATERIALS: readonly { key: MaterialView['key']; icon: string; rarity: MaterialView['rarity'] }[] = [
  { key: 'fangsteel', icon: 'forge_fangsteel', rarity: 'common' },
  { key: 'scale.ashen', icon: 'forge_scale_ashen', rarity: 'common' },
  { key: 'scale.jade', icon: 'forge_scale_jade', rarity: 'uncommon' },
  { key: 'scale.gilded', icon: 'forge_scale_gilded', rarity: 'rare' },
  { key: 'scale.eternal', icon: 'forge_scale_eternal', rarity: 'epic' },
  { key: 'venom', icon: 'forge_venom', rarity: 'uncommon' },
  { key: 'moltskin', icon: 'forge_moltskin', rarity: 'common' },
  { key: 'blessed', icon: 'forge_blessed', rarity: 'rare' },
  { key: 'eyeFragments', icon: 'forge_eye_fragment', rarity: 'legendary' },
  { key: 'sigils', icon: 'forge_sigil', rarity: 'epic' },
  { key: 'vaultTokens', icon: 'forge_vault_token', rarity: 'epic' },
];

/** Every wallet resource as a slot (owned ones first, then empty ones in the fixed order). */
export function materials(p: Profile): MaterialView[] {
  const w = p.wallet;
  const count = (k: MaterialView['key']): number => {
    if (k === 'vaultTokens') return w.vaultTokens?.length ?? 0;
    if (k.startsWith('scale.')) return w.scales[k.slice(6) as keyof typeof w.scales] ?? 0;
    return (w[k as 'fangsteel' | 'venom' | 'moltskin' | 'blessed' | 'eyeFragments' | 'sigils'] as number | undefined) ?? 0;
  };
  const rows = MATERIALS.map((m) => {
    const meta = m.key === 'vaultTokens' ? { label: 'Vault Forge Tokens', use: 'A free craft at its grade (used automatically).' } : RESOURCE_META[m.key];
    return { key: m.key, label: meta.label, icon: m.icon, count: count(m.key), rarity: m.rarity, use: meta.use };
  });
  return [...rows.filter((r) => r.count > 0), ...rows.filter((r) => r.count <= 0)];
}
